import './inhaltsverzeichnis.css';
import React, { useState, useEffect } from "react";
import { ArrowDownUp } from 'lucide-react';
import { nummerAnzeige, nummerMitKuerzel, vergleicheNummer } from '../buecher';
import { useLocation, useNavigate } from "react-router-dom";
import { passtZurSuche, zerlegeNachTreffern } from '../suche';
import { loadLiederListe, subscribeLieder } from "../liederStore";

const edit_api = "/edit_add_api.php";

function InhaltsverzeichnisFav() {
  const [lieder, setLieder] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [messageModal, setMessageModal] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCategory = new URLSearchParams(location.search).get('kategorie') || '';

  const openSong = (id) => {
    sessionStorage.setItem('scrollPosition', window.scrollY);
    sessionStorage.setItem('sortBy', sortBy);
    navigate(`/lied/${id}`);
  };

  const toggleSortBy = () => {
    setSortBy((prevSort) => (prevSort === 'name' ? 'id' : 'name'));
  };

  function checkFavorite(songId) {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    return favorites.includes(String(songId));
  }

  function getFavorites() {
    return JSON.parse(localStorage.getItem('favorites') || '[]').map(String);
  }

  async function exportFavorites() {
    const favorites = getFavorites();

    if (favorites.length === 0) {
      setMessageModal({
        title: '',
        text: 'Du hast noch keine Favoriten zum Exportieren.'
      });
      return;
    }

    setIsExporting(true);

    try {
      const response = await fetch(edit_api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'export_favorites',
          favorites
        })
      });

      const data = await response.json();

      if (!response.ok || !data.code) {
        throw new Error(data.error || 'Export fehlgeschlagen');
      }

      setMessageModal({
        title: 'Dein Export-Code',
        code: data.code,
        text: 'Dieses Backup wird nach 7 Tagen automatisch gelöscht. Nach erfolgreichem Import wird es ebenfalls gelöscht.'
      });
    } catch (error) {
      console.error(error);
      setMessageModal({
        title: '',
        text: 'Der Export hat leider nicht geklappt.'
      });
    } finally {
      setIsExporting(false);
    }
  }

  async function importFavorites() {
    const code = window.prompt('Bitte Export-Nummer eingeben:');
    const normalizedCode = (code || '').trim().toUpperCase();

    if (!normalizedCode) {
      return;
    }

    setIsImporting(true);

    try {
      const response = await fetch(`${edit_api}?action=import_favorites&code=${encodeURIComponent(normalizedCode)}`);
      const data = await response.json();

      if (!response.ok || !Array.isArray(data.favorites)) {
        throw new Error(data.error || 'Import fehlgeschlagen');
      }

      const currentFavorites = getFavorites();
      const mergedFavorites = [...currentFavorites];

      data.favorites.map(String).forEach((favoriteId) => {
        if (!mergedFavorites.includes(favoriteId)) {
          mergedFavorites.push(favoriteId);
        }
      });

      localStorage.setItem('favorites', JSON.stringify(mergedFavorites));
      setMessageModal({
        title: '',
        text: 'Favoriten wurden importiert. Das Backup wurde gelöscht.'
      });
    } catch (error) {
      console.error(error);
      setMessageModal({
        title: '',
        text: 'Diese Nummer wurde nicht gefunden.'
      });
    } finally {
      setIsImporting(false);
    }
  }

  async function copyExportCode() {
    if (!messageModal?.code) {
      return;
    }

    try {
      await navigator.clipboard.writeText(messageModal.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 1800);
    } catch (error) {
      console.error(error);
      setMessageModal({
        ...messageModal,
        text: 'Der Code konnte nicht automatisch kopiert werden. Bitte markiere ihn manuell.'
      });
    }
  }

  useEffect(() => {
    if (!sessionStorage.getItem('scrollPosition')) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    loadLiederListe()
      .then(data => {
        setLieder(data);
        const scrollPos = sessionStorage.getItem('scrollPosition');
        if (scrollPos) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(scrollPos));
            sessionStorage.removeItem('scrollPosition');
          }, 100);
        }
        const savedSortBy = sessionStorage.getItem('sortBy');
        if (savedSortBy) {
          setSortBy(savedSortBy);
          sessionStorage.removeItem('sortBy');
        }
      })
      .catch(err => console.error(err));
  }, []);

  useEffect(() => subscribeLieder(setLieder), []);

  const visibleLieder = lieder
    .filter((lied) => {
      const matchesSearch = passtZurSuche(lied, searchTerm, nummerMitKuerzel(lied));
      const matchesCategory = !selectedCategory || lied.kategorie === selectedCategory;
      if (selectedCategory) {
        return matchesSearch && matchesCategory;
      }
      return checkFavorite(lied.id) && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'id') {
        return vergleicheNummer(a, b);
      }
      return a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
    });

  function renderHighlightedName(name) {
    return zerlegeNachTreffern(name, searchTerm).map((teil, index) =>
      teil.treffer ? <span className='search_match' key={`${name}-${index}`}>{teil.text}</span> : teil.text
    );
  }

  return (
    <div id='inhaltsverzeichnis_body'>
      {messageModal && (
        <div className='modal_overlay' onClick={() => setMessageModal(null)}>
          <div className='modal_dialog' onClick={(event) => event.stopPropagation()}>
            {messageModal.title && <p className='modal_text'>{messageModal.title}</p>}
            {messageModal.code && (
              <div className='favorite_export_code_row'>
                <div className='favorite_export_code'>{messageModal.code}</div>
                <button
                  type='button'
                  className='favorite_copy_button'
                  onClick={copyExportCode}
                  aria-label='Code kopieren'
                  title='Code kopieren'
                >
                  content_copy
                </button>
              </div>
            )}
            {copiedCode && <p className='modal_text favorite_copy_hint'>Code kopiert.</p>}
            <p className='modal_text'>{messageModal.text}</p>
            <button type='button' className='modal_button' onClick={() => setMessageModal(null)}>
              Schließen
            </button>
          </div>
        </div>
      )}
      <div className='heading_box'>
        <h2>Favoriten</h2>
      </div>
      <div className='search_box'>
        <input
          className='searchInput'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder='Lied suchen...'
        />
        <button className='sort_toggle' onClick={toggleSortBy} title='Sortieren nach Nummer oder Titel' aria-label='Sortieren nach Nummer oder Titel'>
          <ArrowDownUp className='sort_icon' />
        </button>
      </div>
      {visibleLieder.map((lied) => (
        <div className='titel_reihe' key={lied.id} onClick={() => openSong(lied.id)}>
          <div className='titelbtn'>
            <div>{renderHighlightedName(lied.name)}</div>
            <div>{nummerAnzeige(lied)}</div>
          </div>
        </div>
      ))}
      <div className='favorite_transfer_actions'>
        <button type='button' onClick={exportFavorites} disabled={isExporting || isImporting}>
          {isExporting ? 'Export...' : 'Export'}
        </button>
        <button type='button' onClick={importFavorites} disabled={isExporting || isImporting}>
          {isImporting ? 'Import...' : 'Import'}
        </button>
      </div>
    </div>
  );
}

export default InhaltsverzeichnisFav;
